import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6aj83ghk82orf0cjt0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);
    const honeypot = String(payload.get("contact_alt") ?? "").trim();

    // Client-side honeypot: silently succeed for bots before any request
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    payload.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg ?? "");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setFormError(serverMsg || "提交失败，请稍后重试");
        setStatus("error");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  const inputClass =
    "h-11 w-full rounded-md border border-background-200 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <form
      data-readdy-form
      id="about-contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-1 text-sm text-foreground-600">
        有影片补充、合作或建议，欢迎留言，我们会尽快回复。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
            咨询类型
          </label>
          <select id="contact-topic" name="topic" required defaultValue="" className={inputClass}>
            <option value="" disabled>
              请选择咨询类型
            </option>
            <option value="影片补充">影片补充</option>
            <option value="内容纠错">内容纠错</option>
            <option value="商务合作">商务合作</option>
            <option value="其他建议">其他建议</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
            留言内容
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={4}
            placeholder="请输入你想说的内容（最多 500 字）"
            className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          ></textarea>
        </div>
      </div>

      {/* Anti-spam field */}
      <input
        className="field-extra-note"
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-base"></i>
              </span>
              提交中...
            </>
          ) : (
            <>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-send-plane-line text-base"></i>
              </span>
              提交留言
            </>
          )}
        </button>

        {status === "success" && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
            <i className="ri-checkbox-circle-fill"></i>
            提交成功，感谢你的留言！
          </p>
        )}
        {status === "error" && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-secondary-600">
            <i className="ri-error-warning-fill"></i>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}